import api, { apiBaseUrl } from './client'
import { getClerkToken } from '@/lib/auth'

export interface HouseholdInfo {
  id: string
  name: string
  role: string
}

export interface CurrentUser {
  id: string
  email: string
  displayName: string | null
  /** Only from a backend older than GET /users/me/password-status; see fetchHasPassword. */
  hasPassword?: boolean
  avatarPreset: string
  household: HouseholdInfo | null
}

/** A round trip to Clerk on the backend, so it's fetched only where it's shown (Security), never
 *  on the /users/me call every app open waits for. */
export async function fetchHasPassword(): Promise<boolean> {
  const { data } = await api.get<{ hasPassword: boolean }>('/users/me/password-status')
  return data.hasPassword
}

export type SignInMethod = 'google' | 'email_password'

export async function fetchCurrentUser(): Promise<CurrentUser> {
  const { data } = await api.get<CurrentUser>('/users/me')
  return data
}

/** Clerk's session token doesn't carry which strategy was used to sign in — the caller already
 *  knows (it's the one that just completed that strategy), so it reports it directly rather than
 *  the backend having to guess.
 *
 *  Deliberately a raw fetch with keepalive, not the shared axios instance: every call site fires
 *  this immediately before navigating away (the Google flow's redirect in particular isn't
 *  guaranteed to be a same-page SPA transition) — a plain XHR request gets cancelled mid-flight
 *  by a page unload, silently dropping the audit record; keepalive survives it. */
export async function recordSession(method: SignInMethod): Promise<void> {
  const token = await getClerkToken()
  await fetch(`${apiBaseUrl}/api/v1/users/me/session`, {
    method: 'POST',
    credentials: 'include',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ method }),
  })
}

export async function signOut(): Promise<void> {
  await api.post('/users/me/sign-out')
}

/** Standard PATCH semantics — omit a field to leave it unchanged. */
export async function updateProfile(payload: {
  displayName?: string
  avatarPreset?: string
}): Promise<CurrentUser> {
  const { data } = await api.patch<CurrentUser>('/users/me', payload)
  return data
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await api.patch('/users/me/password', { currentPassword, newPassword })
}

export async function signOutEverywhere(currentPassword: string): Promise<void> {
  await api.post('/users/me/sign-out-everywhere', { currentPassword })
}

export async function deleteAccount(): Promise<void> {
  await api.delete('/users/me')
}
